/**
 * Shared **HP clamp + delta** helpers — one home for the `[0, maxHp]` clamp every
 * HP-writing path needs. The resolver's damage path and the `set_hp` atom clamp into the
 * same bounds.
 */

import type { Combatant, HpDeltaStep, HeatDeltaStep } from "@lancer-vtt/domain";

/** Clamp a target HP value into `[0, maxHp]` (the one HP-bounds clamp). */
export function clampHp(target: Combatant, hp: number): number {
  return Math.max(0, Math.min(target.base.maxHp, hp));
}

/**
 * Record the **effective** HP delta (post-clamp) for dropping `target` by `amount`
 * damage, as an `hp_delta` step — or `null` if it changes nothing (0 damage, or
 * already at the floor). Recording the effective delta keeps the inverse exact even
 * when the hit clamps the target to 0 (`steps.ts` `HpDeltaStep` note), provided `target` is
 * the combatant as every earlier step of the same resolution left it.
 */
export function hpDeltaStep(target: Combatant, amount: number): HpDeltaStep | null {
  if (amount <= 0) return null;
  const before = target.current.hp;
  const after = clampHp(target, before - amount);
  const delta = after - before;
  if (delta === 0) return null;
  return { op: "hp_delta", target: target.id, delta };
}

/**
 * Record the **effective** heat delta (post-clamp) for adding `amount` heat to
 * `target`'s heat track, as a `heat_delta` step — or `null` if it changes nothing
 * (0 heat, no heat track, or already at the cap). Heat goes onto the heat *track*
 * (`[0, maxHeat]`), never HP, and a combatant with no heat track (`maxHeat`
 * absent) is a no-op. Recording the effective delta keeps the inverse exact at
 * the heat cap, mirroring {@link hpDeltaStep}. The INVADE "2 Heat Damage" emitter.
 */
export function heatDeltaStep(target: Combatant, amount: number): HeatDeltaStep | null {
  if (amount === 0) return null;
  const max = target.base.maxHeat;
  if (max === undefined) return null; // no heat track — heat is a no-op
  const before = target.current.heat ?? 0;
  const after = Math.max(0, Math.min(max, before + amount));
  const delta = after - before;
  if (delta === 0) return null;
  return { op: "heat_delta", target: target.id, delta };
}
